import { linter, lintGutter, type Diagnostic } from '@codemirror/lint';
import type { Extension } from '@codemirror/state';
import { WorkflowDefinitionSchema } from '@/types/workflowDefinition';
import type { ActiveFile } from '../types';

/**
 * Creates a CodeMirror linter extension that validates JSON against workflow schemas.
 * Provides inline error markers and hover tooltips for validation errors.
 */
export const createJsonSchemaLinter = (activeFile: ActiveFile): Extension[] => {
  const jsonLinter = linter(
    (view) => {
      const diagnostics: Diagnostic[] = [];
      const content = view.state.doc.toString();

      // Skip validation for empty content
      if (!content.trim()) {
        return diagnostics;
      }

      // First, validate JSON syntax
      let parsed: unknown;
      try {
        parsed = JSON.parse(content);
      } catch (e) {
        if (e instanceof SyntaxError) {
          // Try to extract position from error message
          const posMatch = e.message.match(/position\s*(\d+)/i);
          const pos = posMatch?.[1] ? parseInt(posMatch[1], 10) : 0;

          // Ensure position is within document bounds
          const safePos = Math.min(pos, view.state.doc.length);
          const endPos = Math.min(safePos + 1, view.state.doc.length);

          diagnostics.push({
            from: safePos,
            to: endPos,
            severity: 'error',
            message: `JSON Syntax Error: ${e.message}`,
            source: 'json',
          });
        }
        return diagnostics;
      }

      // Then validate against schema (workflow.json only for now)
      if (activeFile === 'workflow.json') {
        const result = WorkflowDefinitionSchema.safeParse(parsed);

        if (!result.success) {
          for (const issue of result.error.issues) {
            const pathStr = issue.path.join('.');
            const pos = findJsonPathPosition(content, issue.path);

            diagnostics.push({
              from: pos.from,
              to: pos.to,
              severity: 'error',
              message: pathStr ? `${pathStr}: ${issue.message}` : issue.message,
              source: 'schema',
            });
          }
        }
      }

      // Basic validation for connections.json - must be an object
      if (activeFile === 'connections.json') {
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          diagnostics.push({
            from: 0,
            to: Math.min(10, view.state.doc.length),
            severity: 'error',
            message: 'Connections must be an object',
            source: 'schema',
          });
        }
      }

      return diagnostics;
    },
    {
      delay: 500, // Debounce validation to avoid excessive processing
    }
  );

  return [jsonLinter, lintGutter()];
};

/**
 * Finds the approximate position of a JSON path in the document.
 * Used to position error markers at the relevant location.
 */
const findJsonPathPosition = (
  content: string,
  path: (string | number)[]
): { from: number; to: number } => {
  let pos = 0;

  for (const segment of path) {
    if (typeof segment === 'string') {
      // Look for property key
      const key = `"${segment}"`;
      const idx = content.indexOf(key, pos);
      if (idx !== -1) {
        pos = idx;
      }
    } else if (typeof segment === 'number') {
      // For array indices, try to find the nth element
      // This is a simplified approach - we just move forward
      let bracketCount = 0;
      let elementCount = 0;
      for (let i = pos; i < content.length; i++) {
        const char = content[i];
        if (char === '[') {
          bracketCount++;
        } else if (char === ']') {
          bracketCount--;
        } else if (char === ',' && bracketCount === 1) {
          elementCount++;
          if (elementCount === segment) {
            pos = i + 1;
            break;
          }
        }
      }
    }
  }

  // Find the extent of the value at this position
  const endPos = Math.min(pos + 30, content.length);
  return { from: pos, to: endPos };
};
